/**
 * StatusBadge — Reusable Indicator Component (Elevated)
 *
 * Renders a traffic-light badge (green/amber/red) or a generic status pill.
 * Designed for compact data-dense layouts (Bloomberg style).
 *
 * Status-to-variant mapping exported for consistent usage across pages.
 */

import { clsx } from 'clsx';
import type { TrafficLight } from '@/types';

// ─── Traffic Light Badge ───────────────────────────────────────

interface TrafficLightBadgeProps {
  status: TrafficLight;
  label?: string;
  size?: 'sm' | 'md';
}

const TRAFFIC_STYLES: Record<TrafficLight, string> = {
  green: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  amber: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  red: 'bg-red-500/15 text-red-400 border-red-500/25',
};

const TRAFFIC_DOT: Record<TrafficLight, string> = {
  green: 'bg-emerald-400',
  amber: 'bg-amber-400',
  red: 'bg-red-400',
};

export function TrafficLightBadge({ status, label, size = 'sm' }: TrafficLightBadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        TRAFFIC_STYLES[status],
        size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs',
      )}
    >
      <span
        className={clsx(
          'rounded-full',
          TRAFFIC_DOT[status],
          size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2',
        )}
      />
      {label && <span>{label}</span>}
    </span>
  );
}

// ─── Generic Status Badge (with dot indicator) ─────────────────

type Variant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'muted' | 'purple';

interface StatusBadgeProps {
  label: string;
  variant?: Variant;
  size?: 'sm' | 'md';
  /** Show a leading dot indicator */
  dot?: boolean;
  /** Pulse the dot (for overdue/urgent states) */
  pulse?: boolean;
}

const VARIANT_STYLES: Record<Variant, string> = {
  default: 'bg-slate-500/15 text-slate-300 border-slate-500/25',
  success: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
  warning: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  danger: 'bg-red-500/15 text-red-400 border-red-500/25',
  info: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  muted: 'bg-slate-700/50 text-slate-500 border-slate-600/25',
  purple: 'bg-purple-500/15 text-purple-400 border-purple-500/25',
};

const VARIANT_DOT: Record<Variant, string> = {
  default: 'bg-slate-400',
  success: 'bg-emerald-400',
  warning: 'bg-amber-400',
  danger: 'bg-red-400',
  info: 'bg-blue-400',
  muted: 'bg-slate-500',
  purple: 'bg-purple-400',
};

export function StatusBadge({ label, variant = 'default', size = 'sm', dot = true, pulse = false }: StatusBadgeProps) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-full border font-medium',
        VARIANT_STYLES[variant],
        size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs',
      )}
    >
      {dot && (
        <span
          className={clsx(
            'rounded-full',
            VARIANT_DOT[variant],
            size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2',
            pulse && 'ic-dot-pulse',
          )}
        />
      )}
      {label}
    </span>
  );
}

// ─── Status-to-Variant Mapping (for consistent usage) ─────────

export const STATUS_TO_VARIANT: Record<string, Variant> = {
  // ESG Submission statuses
  draft: 'default',
  submitted: 'info',
  validating: 'purple',
  validated: 'purple',
  approved: 'success',
  rejected: 'danger',
  // Filing statuses
  filed: 'success',
  review: 'info',
  // Fund statuses
  active: 'success',
  fundraising: 'info',
  investing: 'success',
  monitoring: 'warning',
  exiting: 'warning',
  harvesting: 'warning',
  closed: 'muted',
  // Investment statuses
  pipeline: 'default',
  due_diligence: 'info',
  disbursed: 'info',
  restructured: 'warning',
  matured: 'muted',
  written_off: 'danger',
  exited: 'muted',
  // General
  completed: 'success',
  failed: 'danger',
  pending: 'warning',
  overdue: 'danger',
};

// ─── Completion Bar ────────────────────────────────────────────

interface CompletionBarProps {
  percent: number;
  label?: string;
  className?: string;
}

export function CompletionBar({ percent, label, className }: CompletionBarProps) {
  const capped = Math.max(0, Math.min(100, percent));

  const barColor =
    capped >= 80 ? 'bg-emerald-500' : capped >= 50 ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className={clsx('flex flex-col gap-1', className)}>
      {label && (
        <div className="flex items-center justify-between text-[10px] text-ic-text-tertiary">
          <span>{label}</span>
          <span className="font-mono">{capped}%</span>
        </div>
      )}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-700/50">
        <div
          className={clsx('h-full rounded-full transition-[width] duration-500', barColor)}
          style={{ width: `${capped}%` }}
        />
      </div>
    </div>
  );
}
